import React, { useEffect, useMemo, useState } from 'react';
import axios from '../axios';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/ToastProvider';
import StatusBadge from '../components/StatusBadge';
import MobileDataCard from '../components/MobileDataCard';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Payout {
  id: string;
  amount: number;
  status: string;
  createdAt: string;
  processedAt?: string;
}

const Payouts: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [available, setAvailable] = useState<number>(0);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const fetchPayouts = async () => {
    const [payoutRes, availableRes] = await Promise.all([
      axios.get('/api/partner/payouts'),
      axios.get('/api/partner/payouts/available'),
    ]);
    setPayouts(payoutRes.data);
    setAvailable(Number(availableRes.data || 0));
  };

  useEffect(() => {
    fetchPayouts();
  }, []);

  const amountNum = useMemo(() => Number(amount || 0), [amount]);
  const canRequest = amountNum > 0 && amountNum <= available;

  const handleMax = () => {
    setAmount(String(available));
  };

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canRequest) {
      toast.show(t('payouts.partnerInvalidAmount', t('payouts.invalidAmount')) as string, 'info');
      return;
    }
    try {
      setLoading(true);
      await axios.post('/api/partner/payouts', { amount: amountNum });
      toast.show(t('payouts.partnerRequested', t('payouts.requested')) as string, 'success');
      setAmount('');
      fetchPayouts();
    } catch (error: any) {
      toast.show(String(error.response?.data || t('payouts.requestError')), 'error');
    } finally { setLoading(false); }
  };

  return (
    <section>
      <h1 className="text-2xl md:text-3xl font-bold mb-6 text-[#111D4E]">{t('payouts.title')}</h1>
      <form className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden max-w-md mb-6" onSubmit={handleRequest}>
        <div className="px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
          <div className="text-sm font-semibold text-[#111D4E]">{t('payouts.requestTitle')}</div>
          <div className="text-xs text-gray-600">{t('payouts.partnerAvailableNow', t('payouts.availableNow'))} <span className="font-semibold">{formatCurrency(available, i18n.resolvedLanguage)}</span></div>
        </div>
        <div className="p-6">
          <label className="block text-sm font-medium mb-2">{t('payouts.amount')}</label>
          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-md"
              required
            />
            <button
              type="button"
              onClick={handleMax}
              disabled={available <= 0}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md border border-slate-200 disabled:opacity-50"
            >
              {t('payouts.max')}
            </button>
          </div>
          <button
            type="submit"
            disabled={loading || !canRequest}
            className="mt-4 w-full bg-[#47ABDB] disabled:opacity-60 text-white py-2 rounded-md font-semibold hover:bg-[#3d8fb8]"
          >
            {loading ? t('common.loading') : t('payouts.request')}
          </button>
        </div>
      </form>

      <div className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
          <div>
            <div className="text-sm font-semibold text-[#111D4E]">{t('payouts.title')}</div>
            <div className="text-xs text-gray-600">{t('payouts.historySubtitle')}</div>
          </div>
          <button onClick={fetchPayouts} className="px-4 py-2 bg-[#47ABDB] text-white rounded-md text-sm font-semibold shadow-sm hover:bg-[#3d8fb8]">{t('common.refresh')}</button>
        </div>
        <div className="space-y-3 p-4 md:hidden">
          {payouts.length === 0 ? (
            <div className="rounded-2xl border border-slate-200/80 bg-white px-5 py-10 text-center text-base text-gray-500">{t('payouts.empty')}</div>
          ) : (
            payouts.map((payout) => (
              <MobileDataCard
                key={payout.id}
                title={t('payouts.amount')}
                badge={<StatusBadge kind="payout" status={payout.status} labelKeyPrefix="partnerStatus.payout" />}
                subtitle={formatCurrency(payout.amount, i18n.resolvedLanguage)}
                rows={[
                  { label: t('payouts.createdAt'), value: formatDate(payout.createdAt, i18n.resolvedLanguage) },
                  { label: t('payouts.processedAt'), value: payout.processedAt ? formatDate(payout.processedAt, i18n.resolvedLanguage) : '-' },
                ]}
              />
            ))
          )}
        </div>
        <div className="hidden overflow-x-auto md:block">
        <table className="min-w-full text-left table-auto text-[15px] md:text-base">
          <thead className="bg-[#F3F6F9] text-sm uppercase tracking-wide text-[#111D4E]">
            <tr>
              <th className="px-5 py-3 font-semibold">{t('payouts.amount')}</th>
              <th className="px-5 py-3 font-semibold">{t('payouts.status')}</th>
              <th className="px-5 py-3 font-semibold">{t('payouts.createdAt')}</th>
              <th className="px-5 py-3 font-semibold">{t('payouts.processedAt')}</th>
            </tr>
          </thead>
          <tbody>
            {payouts.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-5 py-10 text-center text-base text-gray-500">{t('payouts.empty')}</td>
              </tr>
            ) : (
              payouts.map((payout, idx) => (
                <tr key={payout.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#FBFCFE]'}>
                  <td className="px-5 py-4">{formatCurrency(payout.amount, i18n.resolvedLanguage)}</td>
                  <td className="px-5 py-4">
                    <StatusBadge kind="payout" status={payout.status} labelKeyPrefix="partnerStatus.payout" />
                  </td>
                  <td className="px-5 py-4">{formatDate(payout.createdAt, i18n.resolvedLanguage)}</td>
                  <td className="px-5 py-4">{payout.processedAt ? formatDate(payout.processedAt, i18n.resolvedLanguage) : '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
    </section>
  );
};

export default Payouts;
